/**
 * Gestión → Inventario · **Listado** (`/gestion/inventario`).
 *
 * Puerto de `Inventario::index` + `views/menu/inventario/index.php` y del bundle
 * `assets/js/inventory.bundle.js`: la tabla de productos con su stock
 * (combustibles, lubricantes y extras) y los dos formularios del panel anterior
 * —**registrar entrada** y **registrar salida**— que suman o restan del stock.
 *
 * Cada movimiento se guarda en su cabecera y detalle (`entrada`/`detalle_entrada`
 * y `salida`/`detalle_salida`), con el turno abierto del usuario, y si el producto
 * es un **lubricante** se ajusta también el contador de cada lado de máquina.
 */

import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowLeft,
  ArrowUpFromLine,
  Boxes,
  Package,
  Search,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { InventarioNav } from '../components/inventario-nav';
import {
  useComprasPendientes,
  useInventario,
  useRegistrarEntrada,
  useRegistrarSalida,
} from '../inventario.api';
import type { ProductoInventario } from '../gestion.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Razones que ofrecía el panel anterior en cada formulario. */
const RAZONES_ENTRADA: readonly string[] = [
  'Ingreso temporal',
  'Inventario inicial',
  'Devolución de producto',
  'Prueba de maquina',
];

const RAZONES_SALIDA: readonly string[] = [
  'Salida temporal',
  'Prueba de maquina calibracion',
  'Retiro de galonaje no vendido',
];

/** Fecha y hora de ahora en el formato del campo `datetime-local`. */
function ahoraLocal(): string {
  const fecha = new Date();
  const desplazamiento = fecha.getTimezoneOffset() * 60_000;

  return new Date(fecha.getTime() - desplazamiento).toISOString().slice(0, 16);
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Distintivo del tipo de producto, con el color del catálogo. */
function TipoBadge({ producto }: { producto: ProductoInventario }) {
  const tono =
    producto.tipo === 'COMBUSTIBLE' ? 'brand' : producto.tipo === 'LUBRICANTE' ? 'warning' : 'neutral';

  return <Badge variant={tono}>{producto.tipo}</Badge>;
}

export function InventarioListadoPage() {
  useDocumentTitle('Gestión · Inventario');
  const [busqueda, setBusqueda] = useState('');
  const [tipo, setTipo] = useState('');
  const [dialogo, setDialogo] = useState<'entrada' | 'salida' | null>(null);
  const [productoId, setProductoId] = useState('');
  const [razon, setRazon] = useState(RAZONES_ENTRADA[0] ?? '');
  const [fecha, setFecha] = useState(ahoraLocal());
  const [cantidad, setCantidad] = useState('1');
  const [compraId, setCompraId] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const consulta = useInventario(busqueda, tipo);
  const compras = useComprasPendientes(dialogo === 'entrada' && productoId ? Number(productoId) : null);
  const registrarEntrada = useRegistrarEntrada();
  const registrarSalida = useRegistrarSalida();

  const productos = useMemo(() => consulta.data?.productos ?? [], [consulta.data]);
  const resumen = consulta.data?.resumen;
  const paginacion = usePaginacion(productos, { clave: [busqueda, tipo] });

  const seleccionado = useMemo(
    () => productos.find((producto) => producto.id === Number(productoId)) ?? null,
    [productoId, productos],
  );

  const guardando = registrarEntrada.isPending || registrarSalida.isPending;

  /** Abre el formulario de movimiento con los valores por defecto. */
  function abrir(tipoMovimiento: 'entrada' | 'salida', producto?: ProductoInventario) {
    setDialogo(tipoMovimiento);
    setProductoId(producto ? String(producto.id) : '');
    setRazon((tipoMovimiento === 'entrada' ? RAZONES_ENTRADA[0] : RAZONES_SALIDA[0]) ?? '');
    setFecha(ahoraLocal());
    setCantidad('1');
    setCompraId('');
    setError(null);
    setAviso(null);
  }

  /** Envía el formulario abierto. */
  function guardar() {
    setError(null);

    const monto = Number(cantidad);

    if (!productoId) return setError('Indica el producto.');
    if (!Number.isFinite(monto) || monto <= 0) {
      return setError('La cantidad debe ser un número mayor que cero.');
    }

    const alTerminar = {
      onSuccess: () => {
        setAviso(
          `${dialogo === 'entrada' ? 'Entrada' : 'Salida'} de ${formatNumber(monto)} registrada para ${seleccionado?.nombre ?? 'el producto'}.`,
        );
        setDialogo(null);
      },
      onError: (fallo: unknown) =>
        setError(mensajeDe(fallo, 'No se pudo registrar el movimiento.')),
    };

    if (dialogo === 'entrada') {
      registrarEntrada.mutate(
        {
          producto_id: Number(productoId),
          cantidad: monto,
          razon,
          fecha_entrada: fecha.replace('T', ' '),
          purchase_id: compraId ? Number(compraId) : undefined,
        },
        alTerminar,
      );
    } else {
      registrarSalida.mutate(
        {
          producto_id: Number(productoId),
          cantidad: monto,
          razon,
          fecha_salida: fecha.replace('T', ' '),
        },
        alTerminar,
      );
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/gestion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Gestión
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Boxes className="size-6 text-brand-500" aria-hidden="true" />
          Inventario
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Stock de combustibles, lubricantes y extras, con el registro de entradas y salidas y sus
          históricos.
        </p>
      </div>

      <InventarioNav active="listado" />
    </header>
  );

  if (consulta.error && !consulta.data) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="No se pudo leer el inventario">
          <span className={ICONO_EN_ALERTA}>
            <AlertTriangle className="size-4" aria-hidden="true" />
            {mensajeDe(consulta.error, 'La API no devolvió los productos.')}
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {cabecera}

      {aviso ? (
        <Alert variant="success" title="Inventario actualizado">
          <span className={ICONO_EN_ALERTA}>
            <Package className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {consulta.isLoading ? (
          Array.from({ length: 4 }).map((_, indice) => <Skeleton key={indice} className="h-20 w-full" />)
        ) : (
          <>
            <div className="rounded-card border border-ink-100 bg-white p-4">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Productos
              </span>
              <span className="block text-sm font-semibold text-ink-900">
                {formatNumber(resumen?.total ?? 0)}
              </span>
              <span className="block text-[11px] text-ink-400">
                {formatNumber(resumen?.inactivos ?? 0)} inactivos
              </span>
            </div>
            <div className="rounded-card border border-ink-100 bg-white p-4">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Agotados
              </span>
              <span className="block text-sm font-semibold text-ink-900">
                {formatNumber(resumen?.agotados ?? 0)}
              </span>
              <span className="block text-[11px] text-ink-400">sin stock disponible</span>
            </div>
            <div className="rounded-card border border-ink-100 bg-white p-4">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Bajo mínimo
              </span>
              <span className="block text-sm font-semibold text-ink-900">
                {formatNumber(resumen?.bajo_minimo ?? 0)}
              </span>
              <span className="block text-[11px] text-ink-400">en o por debajo del mínimo de tanque</span>
            </div>
            <div className="rounded-card border border-ink-100 bg-white p-4">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Valor del stock
              </span>
              <span className="block text-sm font-semibold text-ink-900">
                {formatCurrency(resumen?.valor_compra ?? 0)}
              </span>
              <span className="block text-[11px] text-ink-400">a precio de compra</span>
            </div>
          </>
        )}
      </div>

      <Card>
        <CardContent className="space-y-4">
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex min-w-56 flex-col gap-1.5">
                <label htmlFor="inv-buscar" className="text-xs font-semibold text-ink-500">
                  Buscar producto
                </label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-300"
                    aria-hidden="true"
                  />
                  <input
                    id="inv-buscar"
                    className={`${CONTROL} h-10 pl-9`}
                    placeholder="Nombre, PLU o código de barras…"
                    value={busqueda}
                    onChange={(evento) => setBusqueda(evento.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="inv-tipo" className="text-xs font-semibold text-ink-500">
                  Tipo
                </label>
                <Select
                  id="inv-tipo"
                  className={`${CONTROL} h-10 w-44`}
                  value={tipo}
                  onChange={(evento) => setTipo(evento.target.value)}
                >
                  <option value="">Todos</option>
                  {(consulta.data?.tipos ?? []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => abrir('entrada')} disabled={guardando}>
                <ArrowDownToLine className="mr-1.5 size-4" aria-hidden="true" />
                Registrar entrada
              </Button>
              <Button variant="secondary" onClick={() => abrir('salida')} disabled={guardando}>
                <ArrowUpFromLine className="mr-1.5 size-4" aria-hidden="true" />
                Registrar salida
              </Button>
            </div>
          </header>

          {consulta.isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : productos.length === 0 ? (
            <p className="text-sm text-ink-500">Ningún producto coincide con el filtro actual.</p>
          ) : (
            <>
            <div className="max-h-[52vh] overflow-auto rounded-control border border-ink-50">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2 pl-2">Producto</th>
                    <th className="py-2">Tipo</th>
                    <th className="py-2">Unid.</th>
                    <th className="py-2 text-right">Venta</th>
                    <th className="py-2 text-right">Compra</th>
                    <th className="py-2 text-right">Stock</th>
                    <th className="py-2">Estado</th>
                    <th className="w-28 py-2 pr-2 text-right">Movimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {paginacion.visibles.map((producto) => (
                    <tr
                      key={producto.id}
                      className={cn(
                        'border-b border-ink-50 last:border-0',
                        producto.agotado && 'bg-rose-50/40',
                      )}
                    >
                      <td className="max-w-[7rem] py-2 pl-3 text-ink-700">
                        <span className="block truncate" title={producto.nombre}>
                          {producto.nombre}
                        </span>
                        {producto.categoria ? (
                          <span className="block text-[11px] text-ink-400">
                            {producto.categoria}
                            {producto.subcategoria ? ` / ${producto.subcategoria}` : ''}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2">
                        <TipoBadge producto={producto} />
                      </td>
                      <td className="py-2 text-ink-500">{producto.unidad || '—'}</td>
                      <td className="py-2 text-right text-ink-700">
                        {producto.precio_venta === null ? '—' : formatCurrency(producto.precio_venta)}
                      </td>
                      <td className="py-2 text-right text-ink-500">
                        {producto.precio_compra === null ? '—' : formatCurrency(producto.precio_compra)}
                      </td>
                      <td className="py-2 text-right font-medium text-ink-900">
                        {formatNumber(producto.stock)}
                        {producto.porcentaje_tanque !== null ? (
                          <span className="block text-[11px] text-ink-400">
                            {formatNumber(producto.porcentaje_tanque)}% del tanque
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2">
                        {producto.agotado ? (
                          <Badge variant="danger">Agotado</Badge>
                        ) : producto.bajo_minimo ? (
                          <Badge variant="warning">Bajo mínimo</Badge>
                        ) : (
                          <Badge variant="success">Disponible</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => abrir('entrada', producto)}
                            disabled={guardando}
                            title="Registrar una entrada de este producto"
                            aria-label={`Registrar entrada de ${producto.nombre}`}
                          >
                            <ArrowDownToLine className="size-3.5 text-emerald-600" aria-hidden="true" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => abrir('salida', producto)}
                            disabled={guardando}
                            title="Registrar una salida de este producto"
                            aria-label={`Registrar salida de ${producto.nombre}`}
                          >
                            <ArrowUpFromLine className="size-3.5 text-amber-600" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="productos" />
            </>
          )}
        </CardContent>
      </Card>

      <Alert variant="info" title="Cómo se mueve el stock">
        <span className={ICONO_EN_ALERTA}>
          <Wallet className="size-4" aria-hidden="true" />
          Cada entrada suma la cantidad al stock del producto y puede quedar ligada a una compra
          pendiente de almacenar; cada salida la resta. Los dos movimientos guardan el turno abierto
          del usuario y, si el producto es un lubricante, ajustan el contador de cada lado de
          máquina.
        </span>
      </Alert>

      {dialogo ? (
        <Dialog
          open
          onClose={() => setDialogo(null)}
          title={dialogo === 'entrada' ? 'Registrar entrada' : 'Registrar salida'}
          description={
            dialogo === 'entrada'
              ? 'Suma la cantidad al stock del producto.'
              : 'Resta la cantidad del stock del producto.'
          }
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDialogo(null)} disabled={guardando}>
                Cancelar
              </Button>
              <Button onClick={guardar} disabled={guardando}>
                {guardando
                  ? 'Guardando…'
                  : dialogo === 'entrada'
                    ? 'Guardar entrada'
                    : 'Guardar salida'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {error ? (
              <Alert variant="error" title="Revisa los datos">
                {error}
              </Alert>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="mov-producto" className="text-sm font-medium text-ink-700">
                Producto
              </label>
              <Select
                id="mov-producto"
                className={CONTROL}
                value={productoId}
                onChange={(evento) => {
                  setProductoId(evento.target.value);
                  setCompraId('');
                }}
              >
                <option value="">Selecciona el producto…</option>
                {productos.map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.nombre} · {formatNumber(producto.stock)} {producto.unidad}
                  </option>
                ))}
              </Select>
            </div>

            {dialogo === 'entrada' && (compras.data?.compras?.length ?? 0) > 0 ? (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="mov-compra" className="text-sm font-medium text-ink-700">
                  Compra pendiente de almacenar
                </label>
                <Select
                  id="mov-compra"
                  className={CONTROL}
                  value={compraId}
                  onChange={(evento) => setCompraId(evento.target.value)}
                >
                  <option value="">No relacionada</option>
                  {(compras.data?.compras ?? []).map((compra) => (
                    <option key={compra.detalle_id} value={compra.compra_id}>
                      {compra.documento} · {compra.proveedor} · {formatNumber(compra.cantidad)}
                    </option>
                  ))}
                </Select>
                <span className="text-xs text-ink-400">
                  Al guardar, esa línea de la compra quedará marcada como almacenada.
                </span>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="mov-razon" className="text-sm font-medium text-ink-700">
                Razón
              </label>
              <Select
                id="mov-razon"
                className={CONTROL}
                value={razon}
                onChange={(evento) => setRazon(evento.target.value)}
              >
                {(dialogo === 'entrada' ? RAZONES_ENTRADA : RAZONES_SALIDA).map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="mov-fecha" className="text-sm font-medium text-ink-700">
                  Fecha y hora
                </label>
                <CampoFecha
                  id="mov-fecha"
                 
                  className={CONTROL}
                  value={fecha}
                  onChange={(evento) => setFecha(evento.target.value)}
                 tipo="datetime-local" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="mov-cantidad" className="text-sm font-medium text-ink-700">
                  Cantidad {seleccionado?.unidad ? `(${seleccionado.unidad})` : ''}
                </label>
                <input
                  id="mov-cantidad"
                  type="number"
                  step="0.001"
                  min="0.001"
                  className={CONTROL}
                  value={cantidad}
                  onChange={(evento) => setCantidad(evento.target.value)}
                />
              </div>
            </div>

            {seleccionado ? (
              <p className="rounded-control bg-ink-50/70 px-3 py-2 text-xs text-ink-500">
                Stock actual de <strong>{seleccionado.nombre}</strong>:{' '}
                {formatNumber(seleccionado.stock)} {seleccionado.unidad}
                {dialogo === 'salida' && Number(cantidad) > seleccionado.stock ? (
                  <span className="mt-1 block text-rose-600">
                    La cantidad supera el stock: el saldo quedará negativo, como en el panel
                    anterior.
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
